import React, { createContext, useContext, type ReactNode } from "react";
import type { StandardProps } from "@/components/componentMap";
import { FeatureFlagValue } from "@/types/types";

type GalleryContextHelpers = {
  flagEnabled: (key: keyof StandardProps["flags"]) => boolean;
  configEnabled: (key: string) => boolean;
  flagValue: (key: keyof StandardProps["flags"]) => FeatureFlagValue;
  configValue: (key: string) => unknown;
};

type StandardLike = Pick<StandardProps, "product" | "flags" | "configProps"> &
  Partial<Omit<StandardProps, "product" | "flags" | "configProps">>;

export type GalleryContextValue = StandardLike & GalleryContextHelpers;

const GalleryContext = createContext<GalleryContextValue | null>(null);

type ProviderProps = StandardLike & { children: ReactNode };

export function GalleryContextProvider({ children, ...props }: ProviderProps) {
  const flagEnabled = (key: keyof StandardProps["flags"]) =>
    props.flags[key]?.value === true;

  const configEnabled = (key: string) => props.configProps?.[key] === true;

  const flagValue = (key: keyof StandardProps["flags"]) =>
    props.flags[key]?.value;

  const configValue = (key: string) => props.configProps?.[key];

  const value: GalleryContextValue = {
    ...props,
    flagEnabled,
    configEnabled,
    flagValue,
    configValue,
  };

  return (
    <GalleryContext.Provider value={value}>{children}</GalleryContext.Provider>
  );
}

export function useGalleryContext(): GalleryContextValue {
  const ctx = useContext(GalleryContext);
  if (!ctx)
    throw new Error(
      "useGalleryContext must be used within <GalleryContextProvider>",
    );
  return ctx;
}
